<template>
  <v-dialog v-model="dialog" persistent max-width="800px">
    <v-card class="category-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">Edit Category</div>
          <div class="dialog-subtitle">
            Update category image, description, ordering settings, and collection.
          </div>
        </div>

        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body mt-4">
        <v-row dense>
          <v-col cols="12" md="6">
            <div class="form-section">
              <div class="section-title">Image & Details</div>

              <ImageView
                :name="searchName"
                field="category"
                :initUrl="Category.imageUrl"
                @submit="imageChange"
              />

              <div class="field-label mt-3">Category Name</div>
              <v-text-field
                ref="categoryName"
                v-model.trim="Category.name"
                outlined
                dense
                label="Name"
                :readonly="Category.systemKey === 'uncategorized'"
                :error-messages="nameErrors"
                :hide-details="nameErrors.length === 0"
                @change="changeName"
                @focus="$event.target.select()"
              />

              <div class="field-label mt-3">Description</div>
              <v-textarea
                v-model="Category.note"
                counter
                auto-grow
                outlined
                hide-details
                dense
                rows="4"
                :label="$t('labels.description')"
                @focus="$event.target.select()"
              />
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="form-section">
              <div class="section-title">Options</div>

              <div class="setting-row">
                <div>
                  <div class="setting-title">Remote Ordering</div>
                  <div class="setting-caption">
                    Show this category on the remote ordering page.
                  </div>
                </div>

                <v-switch
                  v-model="Category.remoteOrder"
                  color="primary"
                  inset
                  hide-details
                />
              </div>

              <v-divider class="my-3" />

              <div class="setting-row">
                <div>
                  <div class="setting-title">Catalog Ordering</div>
                  <div class="setting-caption">
                    Show this category in catalog ordering for sales reps.
                  </div>
                </div>

                <v-switch
                  v-model="Category.catalogOrder"
                  color="primary"
                  inset
                  hide-details
                />
              </div>

              <v-divider class="my-3" />

              <div class="setting-row">
                <div>
                  <div class="setting-title">Apply Markup</div>
                  <div class="setting-caption">
                    Apply markup to products inside this category.
                  </div>
                </div>

                <v-switch
                  v-model="Category.applyMarkup"
                  color="primary"
                  inset
                  hide-details
                />
              </div>

              <div v-if="Category.applyMarkup">
                <div class="field-label mt-3">Markup Percentage</div>
                <v-text-field
                  v-model="Category.markupPercentage"
                  outlined
                  dense
                  hide-details
                  type="number"
                  min="0"
                  suffix="%"
                  label="Markup Percentage"
                  @focus="$event.target.select()"
                />
              </div>

              <div class="field-label mt-3">Collection</div>
              <v-select
                :items="Collection"
                clearable
                :disabled="
                  currentBusiness &&
                  currentBusiness.metadata &&
                  !currentBusiness.metadata.collections
                "
                hide-details
                v-model="Category.CollectionId"
                item-text="name"
                item-value="id"
                outlined
                dense
                label="Select Collection"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="dialog-actions">
        <v-spacer />
        <v-btn color="primary" text @click="close">Close</v-btn>
        <v-btn
          color="primary"
          rounded
          dark
          :loading="loading"
          @click="submit"
        >
          Update Category
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import CategoryService from "../service.js";
import collectionService from "@/modules/Product/Collection/service.js";
import ImageView from "@/components/Image/ImageView";
import GlobalFunction from "@/mixins/GlobalFunction";
import _ from "lodash";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  name: "update-Category",

  props: {
    editDialog: {
      default: false,
    },
    editCategory: {
      type: Object,
      default: null,
    },
  },

  components: {
    ImageView,
  },

  mixins: [validationMixin, GlobalFunction],

  data() {
    return {
      Collection: [],
      searchName: null,
      loading: false,
      dialog: false,
      Category: this.getEmptyCategory(),
    };
  },

  validations: {
    Category: {
      name: {
        required,
      },
    },
  },

  computed: {
    ...mapGetters("global", ["currentUser", "currentBusiness"]),

    nameErrors() {
      const errors = [];

      if (!this.$v.Category.name.$dirty) {
        return errors;
      }

      if (!this.$v.Category.name.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }

      return errors;
    },
  },

  watch: {
    editDialog(val) {
      this.dialog = val;

      if (val) {
        this.getCollection();

        this.Category = {
          ...this.getEmptyCategory(),
          ..._.cloneDeep(this.editCategory || {}),
          forceApplyMarkup: false,
        };

        this.searchName = this.Category.name;

        this.$v.$reset();

        this.$nextTick(() => {
          if (this.$refs.categoryName) {
            this.$refs.categoryName.focus();
          }
        });
      }
    },
  },

  methods: {
    getEmptyCategory() {
      return {
        id: null,
        name: null,
        note: null,
        imageUrl: null,
        remoteOrder: true,
        catalogOrder: true,
        applyMarkup: false,
        markupPercentage: 0,
        forceApplyMarkup: false,
        CollectionId: null,
      };
    },

    getCollection() {
      this.loading = true;

      return collectionService
        .getAll()
        .then((response) => {
          this.Collection = response.data || [];
          return response;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    close() {
      this.empty();
      this.dialog = false;
      this.$emit("close");
    },

    empty() {
      this.Category = this.getEmptyCategory();
      this.searchName = null;
      this.$v.$reset();
    },

    imageChange(val) {
      this.Category.imageUrl = val || null;
    },

    changeName() {
      this.Category.name = this.properCase(this.Category.name);
      this.searchName = this.Category.name;
    },

    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      if (this.Category.applyMarkup && !this.Category.markupPercentage) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Markup percentage is required",
          });

        return;
      }

      this.loading = true;

      return CategoryService.update(this.Category.id, this.Category)
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Category is updated",
              });

            this.$emit("submit");
            this.close();
          }
        })
        .catch((err) => {
          const message =
            err && err.data && err.data.message
              ? err.data.message
              : "Unable to update category";

          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: message,
            });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.category-dialog-card {
  border-radius: 20px;
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
}

.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.dialog-body {
  padding: 16px;
  overflow-y: auto;
  flex: 1;
}

.form-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
  height: 100%;
}

.section-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 12px;
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.setting-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

.setting-caption {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  max-width: 280px;
}

.dialog-actions {
  padding: 12px 16px;
  flex-shrink: 0;
  background: #ffffff;
}

@media (max-width: 600px) {
  .dialog-header {
    padding: 14px 16px;
  }

  .dialog-title {
    font-size: 18px;
  }

  .form-section {
    padding: 12px;
  }

  .setting-row {
    align-items: flex-start;
  }
}
</style>
